/* =============================================================
   ImportPartnersModal.jsx — AI-powered bulk partner import
   -------------------------------------------------------------
   Supports: .xlsx, .xls, .csv, .pdf, .docx
   Flow:
     1. Admin uploads a file
     2. File parsed / converted to text (or base64 for PDF)
     3. Sent to Supabase Edge Function → Gemini 2.5 Flash
     4. Fuzzy-match each partner name against universities table
        → pre-fills "Connected" status where a match is found
     5. Preview table shown — admin can edit / delete rows
     6. Confirm → bulk insert via addPartners()
   ============================================================= */

import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';

const PARTNER_TYPES = [
  { value: 'ERASMUS',       label: 'Erasmus'       },
  { value: 'BILATERAL',     label: 'Bilateral'     },
  { value: 'DOUBLE DEGREE', label: 'Double Degree' },
];

/* ─── FILE PARSER (same as ImportStudentsModal) ──────────────── */
async function parseFileToContent(file) {
  const ext = file.name.split('.').pop().toLowerCase();

  if (ext === 'pdf') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        resolve({ content: base64, fileType: 'application/pdf' });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  if (ext === 'xlsx' || ext === 'xls') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb  = XLSX.read(e.target.result, { type: 'array' });
          const ws  = wb.Sheets[wb.SheetNames[0]];
          const csv = XLSX.utils.sheet_to_csv(ws);
          resolve({ content: csv, fileType: 'text/csv' });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  if (ext === 'csv' || ext === 'txt') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ content: e.target.result, fileType: 'text/csv' });
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  if (ext === 'docx') {
    const mammoth = await import('mammoth');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const result = await mammoth.extractRawText({ arrayBuffer: e.target.result });
          resolve({ content: result.value, fileType: 'text/plain' });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  throw new Error(`Unsupported file type: .${ext}`);
}

/* ─── FUZZY MATCH against universities table ─────────────────── */
// Common words that are not distinctive enough to match on alone
const STOP_WORDS = new Set([
  'university', 'universität', 'université', 'universidad', 'universidade',
  'universitetet', 'universiteit', 'università',
  'school', 'scuola', 'école', 'escuela',
  'business', 'management', 'economics', 'economic',
  'college', 'institute', 'institution', 'faculty',
  'of', 'the', 'de', 'di', 'del', 'da', 'van', 'von', 'and', 'e',
  'grande', 'haute', 'haute', 'international', 'national', 'royal',
]);

async function matchUniversities(partners) {
  const results = await Promise.all(
    partners.map(async (p) => {
      if (!p.name?.trim()) return { ...p, linked_university_id: null, connection: 'Manual' };

      const words = p.name.trim().split(/\s+/);

      // Build a list of search attempts from most to least specific
      const attempts = [
        words.slice(0, 4).join(' '),           // "Kozminski Business School Warsaw"
        words.slice(0, 3).join(' '),           // "Kozminski Business School"
        words.slice(0, 2).join(' '),           // "Kozminski Business"
        // First distinctive word (>= 4 chars, not a stop word)
        words.find(w => w.length >= 4 && !STOP_WORDS.has(w.toLowerCase())),
      ].filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i); // deduplicate

      try {
        for (const term of attempts) {
          const { data } = await supabase
            .from('universities')
            .select('id, name')
            .ilike('name', `%${term}%`)
            .limit(1)
            .maybeSingle();

          if (data) {
            return { ...p, linked_university_id: data.id, matched_name: data.name, connection: 'Connected' };
          }
        }
      } catch {}

      return { ...p, linked_university_id: null, connection: 'Manual' };
    })
  );
  return results;
}

/* ─── EDITABLE CELL ──────────────────────────────────────────── */
function EditableCell({ value, onChange, type = 'text', options }) {
  if (options) {
    return (
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value || null)}
        className="w-full px-1.5 py-1 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300"
      >
        <option value="">—</option>
        {options.map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
      className="w-full px-1.5 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
    />
  );
}

/* ─── IMPORT PARTNERS MODAL ──────────────────────────────────── */
export default function ImportPartnersModal({ onImport, onClose }) {
  const fileInputRef = useRef(null);

  const [step,        setStep]        = useState('upload');
  const [partners,    setPartners]    = useState([]);
  const [fileName,    setFileName]    = useState('');
  const [error,       setError]       = useState(null);
  const [dragging,    setDragging]    = useState(false);
  const [importError, setImportError] = useState(null);

  /* ── Process file ─────────────────────────────────────────── */
  const processFile = useCallback(async (file) => {
    setFileName(file.name);
    setError(null);
    setStep('loading');

    try {
      const { content, fileType } = await parseFileToContent(file);

      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = supabase.supabaseUrl;

      const res = await fetch(
        `${supabaseUrl}/functions/v1/ai-import-students`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ fileContent: content, fileType, studentType: 'partner' }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        let errMsg = `Edge Function error ${res.status}`;
        try { const j = JSON.parse(text); errMsg = j.error ?? j.message ?? errMsg; } catch {}
        if (text.trimStart().startsWith('<')) {
          errMsg = `Edge Function not reachable (${res.status}). Check that "ai-import-students" is deployed.`;
        }
        throw new Error(errMsg);
      }

      const responseText = await res.text();
      let extracted;
      try { extracted = JSON.parse(responseText).partners; } catch {
        throw new Error(`Invalid response from Edge Function. Got: ${responseText.slice(0, 120)}`);
      }

      if (!extracted || extracted.length === 0) {
        throw new Error('No partner universities found in this file.');
      }

      // Normalize
      const normalized = extracted.map((p, i) => ({
        _id:                    i,
        name:                   p.name                   ?? '',
        country:                p.country                ?? '',
        type:                   p.type                   ?? 'BILATERAL',
        abbreviation:           p.abbreviation           ?? '',
        exchange_period:         p.exchange_period         ?? '',
        campus:                  p.campus                  ?? '',
        business_school:         p.business_school         ?? '',
        language_of_instruction: p.language_of_instruction ?? '',
        contact_name:            p.contact_name            ?? '',
        contact_email:           p.contact_email           ?? '',
        contact_role:            p.contact_role            ?? '',
        useful_links:            Array.isArray(p.useful_links) ? p.useful_links : [],
        key_dates:               Array.isArray(p.key_dates)    ? p.key_dates    : [],
      }));

      // Fuzzy-match against universities table
      const matched = await matchUniversities(normalized);
      setPartners(matched);
      setStep('preview');
    } catch (err) {
      setError(err.message ?? String(err));
      setStep('upload');
    }
  }, []);

  /* ── Drag & drop ──────────────────────────────────────────── */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  /* ── Edit a row ───────────────────────────────────────────── */
  const updateRow = (id, field, value) => {
    setPartners(prev => prev.map(p => p._id === id ? { ...p, [field]: value } : p));
  };

  const deleteRow = (id) => {
    setPartners(prev => prev.filter(p => p._id !== id));
  };

  /* ── Confirm import ───────────────────────────────────────── */
  const handleImport = async () => {
    setImportError(null);
    setStep('importing');
    try {
      const rows = partners
        .filter(p => p.name?.trim())
        .map(p => ({
          name:                    p.name.trim(),
          country:                 p.country               || null,
          type:                    p.type                  || 'BILATERAL',
          abbr:                    p.abbreviation          || null,
          email:                   p.contact_email?.trim() || null,
          connection:              p.connection            || 'Manual',
          linkedUniversityId:      p.linked_university_id  ?? null,
          exchange_period:         p.exchange_period?.trim()         || null,
          campus:                  p.campus?.trim()                  || null,
          business_school:         p.business_school?.trim()         || null,
          language_of_instruction: p.language_of_instruction?.trim() || null,
          useful_links:            p.useful_links ?? [],
          key_dates:               p.key_dates    ?? [],
          // contact_emails array for the partner profile
          contact_emails: p.contact_name || p.contact_email ? [{
            name:  p.contact_name  || '',
            email: p.contact_email || '',
            role:  p.contact_role  || '',
          }] : [],
        }));

      await onImport(rows);
      setStep('done');
      setTimeout(onClose, 1200);
    } catch (err) {
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err);
      setImportError(msg || 'Import failed. Check the console for details.');
      setStep('preview');
    }
  };

  const validCount     = partners.filter(p => p.name?.trim()).length;
  const connectedCount = partners.filter(p => p.connection === 'Connected').length;

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <span className="material-symbols-outlined text-indigo-600 text-[18px]">auto_awesome</span>
            </div>
            <div>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">AI Partner Import</h2>
              <p className="text-xs text-slate-400">Upload any file — AI extracts partner universities automatically</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* ── STEP: UPLOAD ──────────────────────────────────────── */}
        {step === 'upload' && (
          <div className="p-8 flex flex-col gap-6 flex-1">
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-12 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors ${
                dragging ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
              }`}
            >
              <span className="material-symbols-outlined text-[48px] text-slate-300">upload_file</span>
              <div className="text-center">
                <p className="text-sm font-semibold text-[#0b1c30]">Drop your file here or click to browse</p>
                <p className="text-xs text-slate-400 mt-1">Supports Excel (.xlsx), CSV, PDF, Word (.docx) — any language</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.pdf,.docx,.txt"
                className="hidden"
                onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); }}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <span className="material-symbols-outlined text-red-500 text-[16px] mt-0.5 flex-shrink-0">error</span>
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">lock</span>
              File processed securely — AI reads only university names and contact data
            </p>
          </div>
        )}

        {/* ── STEP: LOADING ─────────────────────────────────────── */}
        {step === 'loading' && (
          <div className="p-12 flex flex-col items-center justify-center flex-1 gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-indigo-100" />
              <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px] text-indigo-600">auto_awesome</span>
              </span>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-[#0b1c30]">AI is reading your file…</p>
              <p className="text-xs text-slate-400 mt-1">{fileName}</p>
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 max-w-sm text-center">
              <span className="material-symbols-outlined text-amber-500 text-[16px] mt-0.5 flex-shrink-0">schedule</span>
              <p className="text-xs text-amber-700">
                This may take <strong>1–2 minutes</strong> depending on file size.<br />
                Please don't close or refresh the page.
              </p>
            </div>
          </div>
        )}

        {/* ── STEP: PREVIEW ─────────────────────────────────────── */}
        {step === 'preview' && (
          <>
            <div className="flex items-center gap-3 px-6 py-3 bg-indigo-50 border-b border-indigo-100 flex-shrink-0">
              <span className="material-symbols-outlined text-indigo-500 text-[16px]">info</span>
              <p className="text-xs text-indigo-700 flex-1">
                Review the extracted data. Click any cell to edit.
                {connectedCount > 0 && (
                  <span className="ml-1">
                    <span className="font-semibold text-emerald-700">{connectedCount} partner{connectedCount > 1 ? 's' : ''}</span> matched an existing Unixplore university and will be set as <span className="font-semibold text-emerald-700">Connected</span>.
                  </span>
                )}
              </p>
            </div>

            <div className="overflow-auto flex-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-48">Name *</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-28">Country</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-32">Type</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-20">Abbr.</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-32">Contact Name</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-40">Contact Email</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-28">Role</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-28">Status</th>
                    <th className="px-3 py-2.5 w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {partners.map(p => (
                    <tr key={p._id} className={`${!p.name?.trim() ? 'opacity-40' : ''}`}>
                      <td className="px-3 py-2">
                        <EditableCell value={p.name} onChange={v => updateRow(p._id, 'name', v)} />
                      </td>
                      <td className="px-3 py-2">
                        <EditableCell value={p.country} onChange={v => updateRow(p._id, 'country', v)} />
                      </td>
                      <td className="px-3 py-2">
                        <EditableCell value={p.type} onChange={v => updateRow(p._id, 'type', v)} options={PARTNER_TYPES} />
                      </td>
                      <td className="px-3 py-2">
                        <EditableCell value={p.abbreviation} onChange={v => updateRow(p._id, 'abbreviation', v)} />
                      </td>
                      <td className="px-3 py-2">
                        <EditableCell value={p.contact_name} onChange={v => updateRow(p._id, 'contact_name', v)} />
                      </td>
                      <td className="px-3 py-2">
                        <EditableCell value={p.contact_email} onChange={v => updateRow(p._id, 'contact_email', v)} type="email" />
                      </td>
                      <td className="px-3 py-2">
                        <EditableCell value={p.contact_role} onChange={v => updateRow(p._id, 'contact_role', v)} />
                      </td>
                      <td className="px-3 py-2">
                        {p.connection === 'Connected'
                          ? <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              Connected
                            </span>
                          : <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                              Manual
                            </span>
                        }
                        {p.matched_name && (
                          <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[100px]" title={p.matched_name}>
                            → {p.matched_name}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => deleteRow(p._id)}
                          className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <span className="material-symbols-outlined text-[14px]">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
              <div className="flex items-center gap-4">
                <p className="text-sm text-slate-500">
                  <span className="font-semibold text-[#0b1c30]">{validCount}</span> partner{validCount !== 1 ? 's' : ''} ready to import
                </p>
                {connectedCount > 0 && (
                  <p className="text-xs text-emerald-600">
                    <span className="font-semibold">{connectedCount}</span> Connected · <span className="font-semibold">{validCount - connectedCount}</span> Manual
                  </p>
                )}
                {importError && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">error</span>
                    {importError}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={validCount === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-[16px]">check</span>
                  Import {validCount} Partner{validCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP: IMPORTING ───────────────────────────────────── */}
        {step === 'importing' && (
          <div className="p-12 flex flex-col items-center justify-center flex-1 gap-3">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border-4 border-indigo-100" />
              <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
            </div>
            <p className="text-sm font-medium text-[#0b1c30]">Importing partners…</p>
          </div>
        )}

        {/* ── STEP: DONE ────────────────────────────────────────── */}
        {step === 'done' && (
          <div className="p-12 flex flex-col items-center justify-center flex-1 gap-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-600 text-[24px]">check_circle</span>
            </div>
            <p className="text-sm font-semibold text-emerald-700">Partners imported successfully!</p>
          </div>
        )}

      </div>
    </div>
  );
}
